/**
 * Prüfungen nach dem Arbeitszeitgesetz (ArbZG).
 *
 * Diese Regeln **warnen, sie blockieren nicht**. Wer 10:15 h gearbeitet hat,
 * hat 10:15 h gearbeitet — eine Software, die den Stempel verweigert, erzeugt
 * keine Gesetzestreue, sondern eine falsche Aufzeichnung. Der Verstoss wird
 * sichtbar gemacht und am Tag vermerkt; die Korrektur ist eine arbeits-
 * rechtliche Entscheidung, keine technische.
 *
 * Nicht abgedeckt: der 24-Wochen-Durchschnitt aus § 3 Satz 2 (verlangt eine
 * Betrachtung über Monate statt über einen Tag) und gesetzliche Feiertage
 * nach § 9 — die sind bundeslandabhängig und brauchen einen Feiertagskalender.
 */

import { HOUR_MS, MINUTE_MS, isSunday } from './time.ts';
import { netMs } from './entries.ts';
import type { DayEntry } from './types.ts';

export type ArbzgCode =
  | 'break_missing'
  | 'break_short'
  | 'continuous_six'
  | 'over_eight'
  | 'over_ten'
  | 'rest_period'
  | 'sunday';

export type ArbzgSeverity = 'info' | 'warn' | 'violation';

export interface ArbzgWarning {
  code: ArbzgCode;
  severity: ArbzgSeverity;
  paragraph: string;
  message: string;
}

export const MAX_DAILY_MS = 10 * HOUR_MS;      // § 3 absolute Obergrenze
export const REGULAR_DAILY_MS = 8 * HOUR_MS;   // § 3 Regelarbeitszeit
export const MIN_REST_MS = 11 * HOUR_MS;       // § 5 Ruhezeit
export const MAX_CONTINUOUS_MS = 6 * HOUR_MS;  // § 4 Satz 3
/** § 4 Satz 2: Teilpausen zählen erst ab 15 Minuten. */
export const MIN_BREAK_SEGMENT_MS = 15 * MINUTE_MS;

/** § 4: 30 Minuten ab mehr als 6 h, 45 Minuten ab mehr als 9 h Arbeitszeit. */
export function requiredBreakMinutes(workMs: number): number {
  if (workMs > 9 * HOUR_MS) return 45;
  if (workMs > 6 * HOUR_MS) return 30;
  return 0;
}

/**
 * Anrechenbare Pause: Abschnitte unter 15 Minuten zählen nach § 4 Satz 2
 * nicht als Ruhepause. Eine noch laufende Pause wird mitgerechnet, damit die
 * Anzeige während des Tages nicht fälschlich mahnt.
 */
export function countableBreakMs(entry: Pick<DayEntry, 'breaks'>, now: number): number {
  return (entry.breaks ?? [])
    .map((b) => (b.e ?? now) - b.s)
    .filter((ms) => ms >= MIN_BREAK_SEGMENT_MS)
    .reduce((a, b) => a + b, 0);
}

/** Längster Arbeitsabschnitt am Stück, also zwischen zwei Pausen. */
export function longestStretchMs(entry: DayEntry, now: number): number {
  if (!entry.clockIn) return 0;
  const end = entry.clockOut ?? now;
  const segments = (entry.breaks ?? [])
    .filter((b) => (b.e ?? now) - b.s >= MIN_BREAK_SEGMENT_MS)
    .sort((a, b) => a.s - b.s);

  let longest = 0;
  let cursor = entry.clockIn;
  for (const b of segments) {
    longest = Math.max(longest, b.s - cursor);
    cursor = b.e ?? now;
  }
  return Math.max(longest, end - cursor);
}

/**
 * Prüft einen Tag. `previousClockOut` ist der Feierabend des vorherigen
 * Arbeitstages und wird nur für die Ruhezeit nach § 5 gebraucht.
 */
export function checkDay(
  entry: DayEntry | null | undefined,
  now: number,
  previousClockOut?: number | null,
): ArbzgWarning[] {
  if (!entry?.clockIn) return [];
  const out: ArbzgWarning[] = [];
  const work = netMs(entry, now);
  const finished = entry.clockOut !== null;

  // § 4 — Ruhepausen. Während des laufenden Tages nur hinweisen, sobald der
  // Anspruch entstanden ist; erst zum Feierabend ist es ein echter Verstoss.
  const requiredMin = requiredBreakMinutes(work);
  if (requiredMin > 0) {
    const takenMin = Math.floor(countableBreakMs(entry, now) / MINUTE_MS);
    if (takenMin < requiredMin) {
      out.push({
        code: takenMin === 0 ? 'break_missing' : 'break_short',
        severity: finished ? 'violation' : 'warn',
        paragraph: '§ 4 ArbZG',
        message:
          takenMin === 0
            ? `Ab ${work > 9 * HOUR_MS ? 9 : 6} Stunden Arbeitszeit sind ${requiredMin} Minuten Pause vorgeschrieben — bisher ist keine erfasst.`
            : `Vorgeschrieben sind ${requiredMin} Minuten Pause, erfasst sind ${takenMin}.`,
      });
    }
  }

  // § 4 Satz 3 — nicht länger als sechs Stunden ohne Unterbrechung.
  if (longestStretchMs(entry, now) > MAX_CONTINUOUS_MS) {
    out.push({
      code: 'continuous_six',
      severity: 'warn',
      paragraph: '§ 4 ArbZG',
      message: 'Mehr als sechs Stunden ohne Unterbrechung — die Pause muss innerhalb dieser Zeit liegen.',
    });
  }

  // § 3 — Höchstarbeitszeit.
  if (work > MAX_DAILY_MS) {
    out.push({
      code: 'over_ten',
      severity: 'violation',
      paragraph: '§ 3 ArbZG',
      message: 'Die Höchstarbeitszeit von 10 Stunden ist überschritten.',
    });
  } else if (work > REGULAR_DAILY_MS) {
    out.push({
      code: 'over_eight',
      severity: 'info',
      paragraph: '§ 3 ArbZG',
      message: 'Über 8 Stunden — zulässig, solange der Schnitt über 24 Wochen bei 8 Stunden bleibt.',
    });
  }

  // § 5 — Ruhezeit zum Vortag.
  if (previousClockOut && entry.clockIn - previousClockOut < MIN_REST_MS) {
    const restH = ((entry.clockIn - previousClockOut) / HOUR_MS).toFixed(1).replace('.', ',');
    out.push({
      code: 'rest_period',
      severity: 'warn',
      paragraph: '§ 5 ArbZG',
      message: `Nur ${restH} Stunden Ruhezeit seit dem letzten Feierabend — vorgeschrieben sind 11.`,
    });
  }

  // § 9 — Sonntagsarbeit.
  if (isSunday(entry.date)) {
    out.push({
      code: 'sunday',
      severity: 'info',
      paragraph: '§ 9 ArbZG',
      message: 'Sonntagsarbeit — nur mit Ausnahmetatbestand zulässig, Ersatzruhetag erforderlich.',
    });
  }

  return out;
}

export const highestSeverity = (warnings: ArbzgWarning[]): ArbzgSeverity | null =>
  warnings.some((w) => w.severity === 'violation') ? 'violation'
  : warnings.some((w) => w.severity === 'warn') ? 'warn'
  : warnings.some((w) => w.severity === 'info') ? 'info'
  : null;

/** § 16 Abs. 2: Aufzeichnungen sind zwei Jahre aufzubewahren. */
export const RETENTION_YEARS = 2;
